<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3">{{ $t('users.details.title') }}</h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('loading.message') }}</span>
          </div>
        </div>

        <!-- Not found -->
        <div v-else-if="!user" class="text-center py-4">
          <i class="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5 class="text-warning">{{ $t('users.details.notFound') }}</h5>
        </div>

        <!-- Content -->
        <div v-else class="p-4">
          <div class="row g-3">
            <!-- User Image -->
            <div class="col-12 text-center mb-4">
              <img
                :src="userImageUrl"
                :alt="$t('users.details.avatarAlt')"
                class="user-avatar-details"
                @error="handleImageError"
                style="width: 140px; height: 140px; object-fit: cover; border-radius: 50%; border: 2px solid #e9ecef;"
              />
            </div>

            <!-- Name -->
            <div class="col-md-12">
              <label class="bmd-label-float">{{ $t('users.form.name') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ user.name || $t('users.details.notSpecified') }}
              </div>
            </div>

            <!-- Email -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('users.form.email') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ user.email || $t('users.details.notSpecified') }}
              </div>
            </div>

            <!-- Phone -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('users.form.phone') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ user.phone_num || $t('users.details.notSpecified') }}
              </div>
            </div>

            <!-- Gender -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('users.form.gender') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ user.gender === 'male' ? $t('users.form.male') : $t('users.form.female') }}
              </div>
            </div>

            <!-- Role -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('users.form.role') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ getRoleName(user) }}
              </div>
            </div>

            <!-- Registration Date -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('users.details.registrationDate') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ formatDate(user.created_at) }}
              </div>
            </div>

            <!-- Last Updated -->
            <div class="col-md-6">
              <label class="bmd-label-float">{{ $t('users.details.lastUpdated') }}</label>
              <div class="form-control-plaintext borderless-input">
                {{ formatDate(user.updated_at) }}
              </div>
            </div>

            <!-- ===== Permissions (READ-ONLY, styled like form). Hidden for school_admin ===== -->
            <div class="col-12 mt-4" v-if="!isSchoolAdmin">
              <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <label class="bmd-label-float mb-0">
                    <i class="fas fa-user-shield me-2"></i>
                    {{ $t('users.form.permissions') }}
                    <small class="text-muted ms-2">({{ filteredModuleKeys.length }})</small>
                  </label>

                  <input
                    type="text"
                    class="form-control form-control-sm ms-2 perms-search"
                    v-model.trim="search"
                    :placeholder="tt('permissions.search','Search modules…')"
                    :dir="locale === 'ar' ? 'rtl' : 'ltr'"
                  />
                </div>

                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <small class="text-muted">
                    {{ $t('permissions.totalSelected') }}:
                    {{ totalSelected }} / {{ validSlugSet.size }}
                  </small>
                </div>
              </div>

              <!-- Loading -->
              <div v-if="permsLoading" class="text-center py-3">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">{{ $t('loading.message') }}</span>
                </div>
              </div>

              <!-- Empty -->
              <div v-else-if="filteredModuleKeys.length === 0" class="text-center text-muted py-3">
                {{ $t('permissions.none') }}
              </div>

              <!-- Accordion list (READ-ONLY) -->
              <div v-else class="accordion" id="permsAccordion">
                <div
                  v-for="mod in filteredModuleKeys"
                  :key="mod"
                  class="accordion-item mb-2 shadow-sm border-0 rounded"
                >
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button d-flex align-items-center gap-2 perms-head-btn"
                      type="button"
                      :class="{ collapsed: !openModules.has(mod) }"
                      @click="toggleModule(mod)"
                    >
                      <!-- left side: icon + title -->
                      <span class="mod-left d-flex align-items-center gap-2">
                        <i class="fas fa-cube" />
                        <span class="text-truncate">{{ tt(`sidebar.${mod}`, mod) }}</span>
                      </span>

                      <!-- right edge: “All” indicator + caret -->
                      <span class="mod-right d-flex align-items-center gap-2">
                        <span class="form-check form-switch m-0" @click.stop>
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="rowAllChecked(mod)"
                            disabled
                          />
                        </span>
                        <small class="text-muted">{{ $t('permissions.all') }}</small>

                        <i
                          class="accordion-caret fas"
                          :class="openModules.has(mod) ? 'fa-angle-up' : 'fa-angle-down'"
                          aria-hidden="true"
                        />
                      </span>
                    </button>
                  </h2>

                  <div :class="['accordion-collapse collapse', { show: openModules.has(mod) }]">
                    <div class="accordion-body py-3 bg-white">
                      <div class="row gy-3">
                        <!-- DISPLAY -->
                        <div v-if="actionExists(mod,'display')" class="col-6 col-md-3">
                          <small class="text-muted d-block">{{ $t('permissions.display') }}</small>
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <input type="checkbox" :checked="isAllowed(mod,'display')" disabled />
                            <span :class="badgeClass(isAllowed(mod,'display'), true)">
                              {{ badgeText(isAllowed(mod,'display'), true) }}
                            </span>
                          </div>
                        </div>

                        <!-- CREATE -->
                        <div v-if="actionExists(mod,'create')" class="col-6 col-md-3">
                          <small class="text-muted d-block">{{ $t('permissions.add') }}</small>
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <input type="checkbox" :checked="isAllowed(mod,'create')" disabled />
                            <span :class="badgeClass(isAllowed(mod,'create'), true)">
                              {{ badgeText(isAllowed(mod,'create'), true) }}
                            </span>
                          </div>
                        </div>

                        <!-- UPDATE -->
                        <div v-if="actionExists(mod,'update')" class="col-6 col-md-3">
                          <small class="text-muted d-block">{{ $t('permissions.edit') }}</small>
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <input type="checkbox" :checked="isAllowed(mod,'update')" disabled />
                            <span :class="badgeClass(isAllowed(mod,'update'), true)">
                              {{ badgeText(isAllowed(mod,'update'), true) }}
                            </span>
                          </div>
                        </div>

                        <!-- DELETE -->
                        <div v-if="actionExists(mod,'delete')" class="col-6 col-md-3">
                          <small class="text-muted d-block">{{ $t('permissions.delete') }}</small>
                          <div class="d-flex align-items-center gap-2 flex-wrap">
                            <input type="checkbox" :checked="isAllowed(mod,'delete')" disabled />
                            <span :class="badgeClass(isAllowed(mod,'delete'), true)">
                              {{ badgeText(isAllowed(mod,'delete'), true) }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Selected count for this module -->
                      <div class="mt-3 d-flex align-items-center justify-content-end">
                        <small class="text-muted">
                          {{ $t('permissions.selectedInModule') }}:
                          {{ selectedCountInModule(mod) }} / {{ (actionsByModule.get(mod)?.size || 0) }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /Accordion list -->
            </div>
            <!-- ===== /Permissions ===== -->

            <!-- Actions -->
            <div class="col-12 mt-4">
              <div class="d-flex gap-3">
                <router-link :to="`/users/${user.id}/edit`" class="btn btn-primary flex-grow-1">
                  <i class="fas fa-edit" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                  {{ $t('users.details.edit') }}
                </router-link>
                <button @click="router.back()" class="btn btn-outline-secondary flex-grow-1">
                  <i class="fas fa-arrow-right" :class="locale === 'ar' ? 'ms-2' : 'me-2'"></i>
                  {{ $t('users.details.back') }}
                </button>
              </div>
            </div>
            <!-- /Actions -->
          </div>
        </div>
        <!-- /Content -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import { useToast } from 'vue-toastification'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()
const toast = useToast()
const store = useStore()

const loading = ref(true)
const user = ref(null)

const permsLoading = ref(false)

/* ===== READ-ONLY permissions state (same logic as form) ===== */
const allSlugs = ref([])               // every slug from API
const excludedSlugs = ref(new Set())   // NOT granted for this user

const placeholderImage = '/images/default-avatar.png'
const userImageUrl = computed(() => user.value?.avatar_url || placeholderImage)
const handleImageError = (e) => { e.target.src = placeholderImage }

const formatDate = (dateString) => {
  if (!dateString) return t('users.details.notSpecified')
  const options = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dateString).toLocaleDateString(locale.value?.startsWith('ar') ? 'ar-LY' : 'en-GB', options)
}

const getRoleName = (userData) => {
  const roleName = userData?.role?.name
  if (!roleName) return t('users.details.notSpecified')
  if (typeof roleName === 'string') return roleName
  const langKey = locale.value?.startsWith('ar') ? 'ar' : 'en'
  return roleName[langKey] || roleName.en || roleName.ar || t('users.details.notSpecified')
}

const tt = (key, fallback) => {
  const v = t(key)
  return v === key ? (fallback ?? key) : v
}

/* Sidebar modules per role (same sets you use in the form) */
const ROLE_SIDEBAR_MODULES = {
  school_admin: new Set([
    'admin_dashboard','users','classes','subjects',
    'exams','exam_schedules','assignments','grade_sheets',
    'announcements','student_attendance','teacher_attendance',
    'questions','question_forms',
    'class_fees','financial_settings','payment_receipts','financial_statements',
    'groups','chapters_management','exam_types',
    'canteen_products','canteen_transactions'
  ]),
  academic_manager: new Set([
    'teachers','academic_dashboard','classes','subjects','exam_forms','exam_schedules',
    'grade_sheets','announcements','teacher_attendance','questions','questions_bank','question_forms',
    'groups','chapters_management'
  ]),
  student_affairs: new Set([
    'student_affairs_dashboard','classes','students','guardians','announcements','student_attendance'
  ]),
  teacher: new Set([
    'assignments','exams','exam_schedules','student_attendance','questions','question_forms','grade_sheets'
  ]),
  finance: new Set([
    'class_fees','financial_settings','payment_receipts','financial_statements'
  ]),
  student: new Set([
    'class_schedules','assignments','student_attendance','grade_sheets'
  ]),
  guardian: new Set([
    'student_attendance','payment_receipts'
  ]),
  canteen: new Set([
    'canteen_products','canteen_transactions'
  ])
}

const ROLE_MODULE_ORDER = {
  school_admin: [
    'admin_dashboard','users','classes','students','teachers','guardians','subjects',
    'exams','assignments','grade_sheets','announcements',
    'student_attendance','teacher_attendance',
    'questions','question_forms',
    'class_fees','financial_settings','payment_receipts','financial_statements',
    'groups','chapters_management','exam_types',
    'canteen_products','canteen_transactions'
  ],
  academic_manager: [
    'academic_dashboard','classes','teachers','subjects','exam_schedules','exams','grade_sheets','announcements',
    'teacher_attendance','questions_bank','questions','question_forms','exam_forms','groups','chapters_management'
  ],
  student_affairs: [
    'student_affairs_dashboard','classes','students','guardians','announcements','student_attendance'
  ],
  teacher: ['assignments','exams','student_attendance','questions','question_forms','grade_sheets'],
  finance: ['class_fees','financial_settings','financial_statements','payment_receipts'],
  student: ['class_schedules','assignments','student_attendance','grade_sheets'],
  guardian: ['student_attendance','payment_receipts'],
  canteen: ['canteen_products','canteen_transactions']
}

function sortBySidebarOrder(modKeys, roleSlug) {
  const order = ROLE_MODULE_ORDER[roleSlug] || []
  const idx = new Map(order.map((m, i) => [m, i]))
  return [...modKeys].sort((a, b) => {
    const ia = idx.has(a) ? idx.get(a) : Number.POSITIVE_INFINITY
    const ib = idx.has(b) ? idx.get(b) : Number.POSITIVE_INFINITY
    if (ia !== ib) return ia - ib
    const la = tt(`sidebar.${a}`, a).toString().toLowerCase()
    const lb = tt(`sidebar.${b}`, b).toString().toLowerCase()
    return la.localeCompare(lb)
  })
}

/* Derived role / school_admin flag */
const roleSlug = computed(() => (user.value?.role?.slug || '').trim().toLowerCase())
const isSchoolAdmin = computed(() => roleSlug.value === 'school_admin')

/* Permissions helpers (same as in form, but read-only) */
function parseSlug (s) {
  const parts = String(s).split('.')
  if (parts.length < 2) return { module: s, action: '' }
  const action = parts.pop()
  const module = parts.join('.')
  return { module, action }
}
const validSlugSet = computed(() => new Set(allSlugs.value))

const modulesInApi = computed(() => {
  const s = new Set()
  allSlugs.value.forEach(sl => {
    const { module, action } = parseSlug(sl)
    if (module && action) s.add(module)
  })
  return s
})

const ALL_ROLE_MODULES = computed(() => {
  const u = new Set()
  Object.values(ROLE_SIDEBAR_MODULES).forEach(set => set.forEach(m => u.add(m)))
  return u
})

const visibleModuleKeys = computed(() => {
  if (!roleSlug.value) return []
  const baseSet = roleSlug.value === 'school_admin'
    ? ALL_ROLE_MODULES.value
    : (ROLE_SIDEBAR_MODULES[roleSlug.value] || new Set())
  const primary = []
  baseSet.forEach(m => { if (modulesInApi.value.has(m)) primary.push(m) })
  if (!primary.length && roleSlug.value === 'canteen') {
    const inferred = Array.from(modulesInApi.value).filter(m => m.startsWith('canteen_'))
    return sortBySidebarOrder(inferred, roleSlug.value)
  }
  return sortBySidebarOrder(primary, roleSlug.value)
})

const search = ref('')
const filteredModuleKeys = computed(() => {
  const base = visibleModuleKeys.value
  if (!search.value) return base
  const needle = search.value.toLowerCase()
  return base.filter(m => (tt(`sidebar.${m}`, m)).toString().toLowerCase().includes(needle))
})

const actionsByModule = computed(() => {
  const map = new Map()
  allSlugs.value.forEach(s => {
    const { module, action } = parseSlug(s)
    if (!module || !action) return
    if (!map.has(module)) map.set(module, new Set())
    map.get(module).add(action)
  })
  return map
})

/* Accordion state */
const openModules = ref(new Set())
const toggleModule = (m) => {
  const set = new Set(openModules.value)
  set.has(m) ? set.delete(m) : set.add(m)
  openModules.value = set
}

const slug = (m, a) => `${m}.${a}`
const actionExists = (m, a) => validSlugSet.value.has(slug(m, a))

/* Allowed? (granted = not in excluded) */
const isAllowed = (m, a) => !excludedSlugs.value.has(slug(m, a))

const badgeClass = (ok, exists = true) => {
  if (!exists) return 'badge bg-secondary-subtle text-muted border'
  return ok ? 'badge bg-success-subtle text-success border' : 'badge bg-danger-subtle text-danger border'
}
const badgeText = (ok, exists = true) => {
  if (!exists) return tt('permissions.not_available', 'N/A')
  return ok ? tt('permissions.allowed', 'نعم') : tt('permissions.denied', 'لا')
}

const rowAllChecked = (m) => {
  const acts = actionsByModule.value.get(m) || new Set()
  if (!acts.size) return false
  for (const a of acts) {
    if (!isAllowed(m, a)) return false
  }
  return true
}

const selectedCountInModule = (m) => {
  const acts = actionsByModule.value.get(m) || new Set()
  let sel = 0
  acts.forEach(a => { if (isAllowed(m, a)) sel++ })
  return sel
}
const totalSelected = computed(() =>
  allSlugs.value.reduce((acc, s) => acc + (excludedSlugs.value.has(s) ? 0 : 1), 0)
)

/* API */
async function loadUserPermissions (userId) {
  permsLoading.value = true
  try {
    const { data } = await api.get(`/users/${userId}/permissions`)
    const all = (data?.all ?? []).map(p => String(p.slug))
    const granted = Array.isArray(data?.granted) ? data.granted.map(String) : []
    allSlugs.value = all
    excludedSlugs.value = new Set(all.filter(s => !granted.includes(s)))
    // open first few accordions by default
    openModules.value = new Set((filteredModuleKeys.value || []).slice(0, 6))
  } finally {
    permsLoading.value = false
  }
}

/* Mount */
onMounted(async () => {
  try {
    const userId = route.params.id
    if (!userId) { router.push('/users'); return }

    if (!store.getters.getRoles?.length) {
      await store.dispatch('fetchRoles') // keeps role names in store if needed
    }
    const response = await api.get(`/users/${userId}`)
    user.value = response.data?.user || response.data
    loading.value = false

    // Load permissions only if NOT school_admin (we hide them anyway, but we can skip the call)
    if (user.value?.id && (user.value?.role?.slug || '').toLowerCase() !== 'school_admin') {
      await loadUserPermissions(user.value.id)
    }
  } catch (error) {
    toast.error(t('users.details.loadError'))
    console.error('Error loading user:', error)
    router.push('/users')
  }
})
</script>

<style scoped>
.borderless-input {
  border: none;
  border-bottom: 1px solid #ced4da;
  border-radius: 0;
  padding-left: 0;
}
.borderless-input:focus { box-shadow: none; border-bottom-color: #007bff; }

.table th, .table td { vertical-align: middle; }

.bg-success-subtle { background-color: #d1e7dd !important; }
.bg-danger-subtle  { background-color: #f8d7da !important; }
.bg-secondary-subtle { background-color: #e2e3e5 !important; }

.perms-search{ min-width:240px; }

.accordion-item{
  background:#fff;
  border:1px solid #e9ecef !important;
  border-radius:.5rem !important;
}
.accordion-button{
  font-weight:600;
  background:#f8f9fa;
  border-radius:.5rem !important;
}
.accordion-button:focus{ box-shadow:none; }
.accordion-button:not(.collapsed){ background:#eef3ff; }

.badge{ font-weight:600; }

/* head layout like the form */
.perms-head-btn::after { display: none; }
.perms-head-btn .mod-left { flex: 1 1 auto; min-width: 0; }
.perms-head-btn .mod-right { flex: 0 0 auto; margin-inline-start: auto; gap: .75rem; }
.perms-head-btn .accordion-caret { font-size: 0.9rem; }

/* RTL polish */
[dir="rtl"] .perms-head-btn .mod-left i { margin-left: 0; margin-right: 0; }
</style>
